import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { RegionProvider } from './contexts/RegionContext';
import AutoTranslate from './components/AutoTranslate';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PageHighlightStrip from './components/PageHighlightStrip';
import VisitorTracker from './components/VisitorTracker';
import SupportChat from './components/SupportChat';
import Home from './pages/Home';
import { ServicesIndex, ServiceDetail } from './pages/Services';
import { LandSurveyingPage, SurveyTypeDetail } from './pages/LandSurveying';
import { AboutUs, TeamPage, TeamMemberDetail, VisionMission, WhyUs, IndustriesPage, IndustryDetail } from './pages/About';
import { InsightsPage, CareersPage, ContactPage, GetQuotePage, PrivacyPage, TermsPage, CoveragePage } from './pages/Other';
import { BlogsPage, NewsPage } from './pages/BlogsNews';
import InsightDetailLive from './pages/InsightDetailLive';
import Admin from './pages/Admin';

const FocusedLandingPage = React.lazy(() => import('./pages/FocusedLandingPage'));
const GeoUSA = React.lazy(() => import('./pages/GeoUSA').then(m => ({ default: m.ServiceAreasIndex })));
const StatePage = React.lazy(() => import('./pages/GeoUSA').then(m => ({ default: m.StatePage })));
const StateServicePage = React.lazy(() => import('./pages/GeoUSA').then(m => ({ default: m.StateServicePage })));
const CountyPage = React.lazy(() => import('./pages/GeoUSA').then(m => ({ default: m.CountyPage })));
const CountryHub = React.lazy(() => import('./pages/GeoIntl').then(m => ({ default: m.CountryHub })));
const CountryPage = React.lazy(() => import('./pages/GeoIntl').then(m => ({ default: m.CountryPage })));
const MontrealFr = React.lazy(() => import('./pages/GeoIntl').then(m => ({ default: m.MontrealFr })));
const GlobalCountryPage = React.lazy(() => import('./pages/GlobalCountryPage'));

const ALIAS_REDIRECTS = {
  '/topographic-survey-drafting': '/services/land-surveying/topographic-surveys',
  '/boundary-survey-drafting': '/services/land-surveying/boundary-surveys',
  '/alta-nsps-survey-drafting': '/services/land-surveying/alta-surveys',
  '/as-built-survey-drafting': '/services/land-surveying/as-built-surveys',
  '/outsourced-cad-drafting': '/survey-cad-outsourcing',
  '/cad-drafting-for-surveyors': '/survey-cad-outsourcing'
};

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#0e1116]">
    <div className="w-8 h-8 border-2 border-gray-700 border-t-orange-500 rounded-full animate-spin"></div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <RegionProvider>
          <BrowserRouter>
            <ScrollToTop />
            <VisitorTracker />
            <AutoTranslate>
              <Navbar />
              <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<ServicesIndex />} />
                <Route path="/services/land-surveying" element={<LandSurveyingPage />} />
                <Route path="/services/land-surveying/:subSlug" element={<SurveyTypeDetail />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/about/team" element={<TeamPage />} />
                <Route path="/about/team/:slug" element={<TeamMemberDetail />} />
                <Route path="/about/vision" element={<VisionMission />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/industries/:slug" element={<IndustryDetail />} />
                <Route path="/coverage" element={<CoveragePage />} />
                <Route path="/why-us" element={<WhyUs />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/blogs" element={<BlogsPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/insights/:slug" element={<InsightDetailLive />} />
                <Route path="/blogs/:slug" element={<InsightDetailLive />} />
                <Route path="/news/:slug" element={<InsightDetailLive />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/get-quote" element={<GetQuotePage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/admin" element={<Admin />} />
                {/* SEO: focused landing pages + aliases */}
                <Route path="/survey-cad-outsourcing" element={<FocusedLandingPage />} />
                <Route path="/gis-mapping-services" element={<FocusedLandingPage />} />
                <Route path="/lidar-point-cloud-to-cad" element={<FocusedLandingPage />} />
                <Route path="/survey-data-conversion" element={<FocusedLandingPage />} />
                <Route path="/civil-3d-survey-drafting" element={<FocusedLandingPage />} />
                <Route path="/outsourced-bim-services" element={<FocusedLandingPage />} />
                <Route path="/mep-drafting-services" element={<FocusedLandingPage />} />
                <Route path="/plant-engineering-design-services" element={<FocusedLandingPage />} />
                {Object.entries(ALIAS_REDIRECTS).map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                {/* SEO: US local pages */}
                <Route path="/service-areas" element={<GeoUSA />} />
                <Route path="/service-areas/:state" element={<StatePage />} />
                <Route path="/service-areas/:state/county/:county" element={<CountyPage />} />
                <Route path="/service-areas/:state/:service" element={<StateServicePage />} />
                {/* SEO: international markets */}
                <Route path="/canada/montreal/fr" element={<MontrealFr />} />
                <Route path="/global/:country" element={<GlobalCountryPage />} />
                <Route path="/usa" element={<CountryHub />} />
                <Route path="/canada" element={<CountryHub />} />
                <Route path="/uk" element={<CountryHub />} />
                <Route path="/australia" element={<CountryHub />} />
                <Route path="/:country/:slug" element={<CountryPage />} />
              </Routes>
              </React.Suspense>
              <PageHighlightStrip />
              <Footer />
              <SupportChat />
            </AutoTranslate>
          </BrowserRouter>
        </RegionProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
